import { useTranslation } from 'next-i18next';
import { Dealer, DealerDetailsDisplayConfig } from '../dealer.types';
import { cx } from '@emotion/css';
import { useGtmTracking } from '../hooks/use-gtm-tracking';
import { DSLinkButton } from '@stihl-design-system/components';

export const DealerActionButtons = ({ dealer, displayConfig }: { dealer: Dealer; displayConfig: DealerDetailsDisplayConfig }) => {
  const { t } = useTranslation(['shared']);
  const { trackPromotion } = useGtmTracking();
  if (!dealer?.location?.googleMapsUrl && !dealer?.contact?.publicEmail && !dealer?.contact?.website) {
    return null;
  }

  return (
    <section className="space-y-4">
      <div
        className={cx('inline-flex flex-col gap-4 tablet:flex-row tablet:flex-wrap mobileOnly:w-full')}
        data-testid="dealer-details_action-buttons"
      >
        <div className="inline-flex flex-col gap-4 miniTablet:flex-row miniTablet:flex-wrap">
          {dealer?.location?.googleMapsUrl && displayConfig.planRouteButton && (
            <DSLinkButton
              variant="outline"
              size="small"
              href={dealer?.location?.googleMapsUrl}
              target="_blank"
              rel="noreferrer"
              iconName="wind-rose"
              iconPosition="left"
              data-testid="dealer-details_button-routing"
              className="grow"
              onClick={() => trackPromotion(dealer, 'route-planer')}
              aria-label={
                t('shared_dealers_map_detail_button_planRoute_aria', {
                  dealer: dealer.description,
                }) + ` (${t('shared_a11y_hint_opensInNewTab')})`
              }
            >
              {t('shared_dealers_map_detail_button_planRoute')}
            </DSLinkButton>
          )}
          {dealer?.contact?.publicEmail && displayConfig.writeEmailButton && (
            <DSLinkButton
              variant="outline"
              size="small"
              href={`mailto:${dealer?.contact?.publicEmail}`}
              target="_blank"
              rel="noreferrer"
              iconName="envelope"
              iconPosition="left"
              data-testid="dealer-details_button-email"
              className="grow"
              onClick={() => trackPromotion(dealer, 'mailto')}
              aria-label={
                t('shared_dealers_map_detail_button_writeMail_aria', {
                  dealer: dealer.description,
                }) + ` (${t('shared_a11y_hint_opensInNewTab')})`
              }
            >
              {t('shared_dealers_map_detail_button_writeMail')}
            </DSLinkButton>
          )}
          {dealer?.contact?.website && displayConfig.visitWebsiteButton && (
            <DSLinkButton
              variant="outline"
              size="small"
              href={!dealer?.contact?.website?.startsWith('http') ? `https://${dealer?.contact?.website}` : dealer?.contact?.website}
              target="_blank"
              rel="noreferrer"
              iconName="globe"
              iconPosition="left"
              data-testid="dealer-details_button-website"
              className="grow"
              onClick={() => trackPromotion(dealer, 'linkto')}
              aria-label={
                t('shared_dealers_map_detail_button_visitWebsite_aria', {
                  dealer: dealer.description,
                }) + ` (${t('shared_a11y_hint_opensInNewTab')})`
              }
            >
              {t('shared_dealers_map_detail_button_visitWebsite')}
            </DSLinkButton>
          )}
        </div>
      </div>
    </section>
  );
};
